import React, {FC, useState} from "react"

import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {Body, H4, H5} from "@irati/ui/src/Typography"
import FormattedMessage from "@irati/utils/lang"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import adminTracking from "@irati-tracking/admin"

import RadialSteps from "../../RadialSteps"
import {Container, FlexItem} from "./styled"
import {Action, ONBOARDING_STARTED_EVENT, Step} from "./OnboardingSteps"
import OnboardingSlider from "./OnboardingSlider"

enum Mode {
  view = "view",
  slide = "slide",
  finish = "finish"
}

interface Props {
  step: Step
}

const OnboardingStep: FC<Props> = ({step}) => {
  const id = step.id
  const title = step.title
  const actions = step.actions as Action[]
  const completed = actions.filter((a) => a.complete === true).length

  const [currentMode, setCurrentMode] = useState<Mode>(actions.length === completed ? Mode.finish : Mode.view)
  const [resumed, setResumed] = useState<boolean>(false)

  const handleStart = () => {
    adminTracking.adminDashboardOnboardingV2Action(step.event, ONBOARDING_STARTED_EVENT)
    setCurrentMode(Mode.slide)
  }

  const handleResume = () => {
    setResumed(true)
    setCurrentMode(Mode.view)
  }

  return (
    <Container
      flex="1"
      direction="column"
      justifyContent="space-between"
      minHeight="300px"
      padding={currentMode === Mode.finish ? [2] :[2, 1, 2, 2]}
      completed={currentMode === Mode.view ? resumed : false}>
      {(() => {
        switch (currentMode) {
          case Mode.view:
            return (
              <>
                <Flex justifyContent="space-between">
                  <FlexItem color={theme.colors.filterChip}>
                    <FormattedMessage id={title} />
                  </FlexItem>
                  <RadialSteps
                    completed={completed}
                    steps={actions.length}
                    fill={resumed ? theme.colors.greys.graphGrey : theme.colors.greys.lightest}
                    small />
                </Flex>
                <Flex direction="column" marginBottom marginTop>
                  {actions?.map((item: Action) => {
                    const actionTitle = item.title
                    return (
                      <Flex alignItems="center" marginBottom>
                        <Icon icon={item.complete ? IconTypes.circleCheckFill : IconTypes.circle} marginRight />
                        <Body width="80%">
                          <FormattedMessage id={actionTitle} />
                        </Body>
                      </Flex>
                    )
                  })}
                </Flex>
                <Flex justifyContent="flex-end">
                  <Button id={`adminSection_getStarted_${id}`} primary small onClick={handleStart} marginRight>
                    {resumed
                      ? <FormattedMessage id="admin.components.onboardingProgress.setup.resumeBtn" />
                      : <FormattedMessage id="admin.components.onboardingProgress.setup.btn" />}
                  </Button>
                </Flex>
              </>
            )
          case Mode.slide:
            return (
              <OnboardingSlider
                title={title}
                event={step.event}
                slides={resumed ? actions : actions.filter((a) => a.complete === false)}
                total={actions.length}
                completed={completed}
                onCallback={() => setCurrentMode(Mode.view)} />
            )
          case Mode.finish:
            return (
              <Flex justifyContent="center" direction="column" height="100%">
                <H4 textAlign="center">
                  <FormattedMessage id="admin.components.onboardingProgress.setup.doneTitle2" />
                </H4>
                <H5 textAlign="center" marginBottom={3}>
                  <FormattedMessage id="admin.components.onboardingProgress.setup.doneTitle1" />
                </H5>
                <Flex alignSelf="center">
                  <Button small primary onClick={handleResume} marginRight>
                    <FormattedMessage id="admin.components.onboardingProgress.setup.doneBtn" />
                  </Button>
                </Flex>
              </Flex>
            )
          default:
            return null
        }
      })()}
    </Container>
  )
}

export default OnboardingStep
